import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup, { type ButtonGroupProps } from '@mui/material/ButtonGroup';
import { UMSelectIcon, UMDrawPolygonIcon, UMSquareIcon } from '@urbanmetrix/um-kit';
import CustomTooltip from 'components/tooltip/CustomTooltip';

type BorderShapeType = 'polygon' | 'rectangle';
type BorderType = 'legalBorder' | 'buildableBorder' | 'analysisArea';
type DrawBorderType = BorderType | 'customPolygonForTarget';

interface CreateBorderButtonProps {
  isReview?: boolean;
  // ButtonGroup's color union is stricter than Button's (no custom `neutral`);
  // use it so `color` type-checks on both the group and its child buttons.
  color: ButtonGroupProps['color'];
  startDrawing: (shapeType: BorderShapeType | null, borderType?: BorderType | null) => void;
  borderType: DrawBorderType;
  btnText: string;
  handleOpenModalForAnalysisAreaType?: () => void;
  onDrawRequest?: (shapeType: BorderShapeType, borderType: BorderType) => void;
  tooltipTitle?: string;
  showSelectButton?: boolean;
  leadingActionButton?: ReactNode;
  onBeforeAction?: () => void;
  handleSelectClick?: () => void;
  isSelectActive?: boolean;
  isSelectDisabled?: boolean;
  selectActionIcon?: ReactNode;
  hideLabelChip?: boolean;
}

const CreateBorderButton = ({
  isReview = false,
  color,
  startDrawing,
  borderType,
  btnText,
  handleOpenModalForAnalysisAreaType,
  onDrawRequest,
  tooltipTitle,
  showSelectButton = false,
  leadingActionButton = null,
  onBeforeAction,
  handleSelectClick,
  isSelectActive = false,
  isSelectDisabled = false,
  selectActionIcon,
  hideLabelChip = false,
}: CreateBorderButtonProps) => {
  const handleDrawClick = (shapeType: 'polygon' | 'rectangle') => {
    onBeforeAction?.();
    if (borderType === 'legalBorder' || borderType === 'buildableBorder' || borderType === 'analysisArea') {
      if (onDrawRequest) {
        onDrawRequest(shapeType, borderType);
        return;
      }
      startDrawing(shapeType, borderType);
    }

    if (borderType === 'customPolygonForTarget') {
      startDrawing(shapeType);
    }
  };

  return (
    <div className="border-action-group">
      <div className="border-action-controls">
        {!hideLabelChip && <div className={`border-label-chip border-label-chip--${color}`}>{btnText}</div>}
        <ButtonGroup variant="contained" color={color}>
          {leadingActionButton}
          {showSelectButton && (
            <Button
              size="small"
              color={color}
              disabled={isReview || isSelectDisabled}
              className={`border-action-icon-white ${isSelectActive ? 'save-button-active' : ''} ${isReview || isSelectDisabled ? 'border-action-disabled' : ''}`.trim()}
              data-border-action="select"
              onClick={() => {
                if (handleSelectClick) {
                  handleSelectClick();
                  return;
                }
                onBeforeAction?.();
                handleOpenModalForAnalysisAreaType?.();
              }}
            >
              {selectActionIcon ?? <UMSelectIcon className="action-icons" color="#fff" />}
            </Button>
          )}
          <Button
            size="small"
            color={color}
            className={`border-action-icon-white ${isReview ? 'border-action-disabled' : ''}`.trim()}
            disabled={isReview}
            data-draw-border-type={borderType}
            data-draw-shape="polygon"
            data-draw-source="polygon"
            onClick={() => handleDrawClick('polygon')}
          >
            <UMDrawPolygonIcon className="action-icons" color="#fff" />
          </Button>
          {borderType !== 'customPolygonForTarget' && (
            <Button
              size="small"
              color={color}
              className={`border-action-icon-white ${isReview ? 'border-action-disabled' : ''}`.trim()}
              disabled={isReview}
              data-draw-border-type={borderType}
              data-draw-shape="rectangle"
              data-draw-source="rectangle"
              onClick={() => handleDrawClick('rectangle')}
            >
              <UMSquareIcon className="action-icons" color="#fff" />
            </Button>
          )}
        </ButtonGroup>
      </div>

      {tooltipTitle ? <CustomTooltip title={tooltipTitle} /> : null}
    </div>
  );
};

export default CreateBorderButton;
